import { useMutation } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { getIgrejaIdByDomain } from '@/lib/igreja'

interface RegisterData {
  name: string
  email: string
  phone: string
  password: string
}

interface SocialRegisterData {
  provider: 'google'
}

interface RegisterResponse {
  success: boolean
  message: string
  user_id?: string
  error?: string
}

export function useRegister() {
  const registerMutation = useMutation({
    mutationFn: async (data: RegisterData): Promise<RegisterResponse> => {
      try {
        if (!supabase) {
          throw new Error('Supabase não está disponível')
        }

        // 1. Detectar a igreja baseada no domínio atual
        const igrejaId = await getIgrejaIdByDomain()
        
        if (!igrejaId) {
          throw new Error('Não foi possível identificar a igreja para este domínio')
        }

        // 2. Criar usuário no Supabase Auth
        const { data: authData, error: authError } = await supabase.auth.signUp({
          email: data.email,
          password: data.password,
        })

        if (authError) {
          throw new Error(`Erro ao criar conta: ${authError.message}`)
        }

        if (!authData.user) {
          throw new Error('Erro inesperado ao criar usuário')
        }

        // 3. Criar registro na tabela usuarios com a igreja detectada
        const { error: usuarioError } = await supabase
          .from('usuarios')
          .insert({
            auth_user_id: authData.user.id,
            nome: data.name,
            email: data.email,
            telefone: data.phone,
            igreja_id: igrejaId,
            permissao: 'membro', // Permissão padrão para novos registros
            ativo: true,
            primeiro_acesso: false, // Usuário já definiu senha própria
          })

        if (usuarioError) {
          // Se falhou ao criar na tabela, tentar remover do Auth
          try {
            await supabase.auth.admin.deleteUser(authData.user.id)
          } catch (cleanupError) {
            console.error('Erro ao limpar usuário do Auth:', cleanupError)
          }
          
          throw new Error(`Erro ao salvar dados do usuário: ${usuarioError.message}`)
        }

        return {
          success: true,
          message: 'Usuário registrado com sucesso',
          user_id: authData.user.id,
        }
      } catch (error) {
        console.error('Erro no registro:', error)
        return {
          success: false,
          message: '',
          error: error instanceof Error ? error.message : 'Erro desconhecido no registro',
        }
      }
    },
  })

  const registerWithSocialMutation = useMutation({
    mutationFn: async (data: SocialRegisterData): Promise<RegisterResponse> => {
      try {
        if (!supabase) {
          throw new Error('Supabase não está disponível')
        }

        // Construir URL de redirecionamento
        const redirectTo = typeof window !== 'undefined'
          ? `${window.location.origin}/auth/callback`
          : undefined

        // Iniciar OAuth
        const { data: authData, error: authError } = await supabase.auth.signInWithOAuth({
          provider: data.provider,
          options: redirectTo ? { redirectTo } : undefined,
        })

        if (authError) {
          throw new Error(`Erro ao conectar com ${data.provider}: ${authError.message}`)
        }

        if (typeof window !== 'undefined' && authData?.url) {
          window.location.href = authData.url
        }

        return {
          success: true,
          message: `Redirecionando para ${data.provider}...`,
        }
      } catch (error) {
        console.error('Erro no registro social:', error)
        return {
          success: false,
          message: '',
          error: error instanceof Error ? error.message : 'Erro desconhecido no registro social',
        }
      }
    },
  })

  return {
    register: registerMutation.mutateAsync,
    registerWithSocial: registerWithSocialMutation.mutateAsync,
    isRegistering: registerMutation.isPending || registerWithSocialMutation.isPending,
  }
}